import { setSyncProgress, store } from '@/app/store';
import { formatUserIdToJid } from '@/features/chats';
import {
   addMessages,
   prependMessages,
   setConversationHasMore,
   setConversationMessages,
   setRepliedMessages,
} from '@/features/chats/model';
import { groupMediaMessages } from '@/features/chats/utils/mediaGrouping';
import { updateInviteStatus } from '@/features/invite/model/inviteSlice';
import { getChatMessages } from '@/infrastructure/sdk';
import { createLogger } from '@/infrastructure/sync/utils';
import { i18n } from '@/localization/i18n';
import { SYNC_CONSTANTS } from '@/shared/constants';
import {
   deduplicateMessages,
   getLocalUserId,
   getMessageSenderId,
   getUserIdFromJid,
   insertDateLabels,
} from '@/shared/utils';
import storage from '@/shared/utils/storage';

// ============================================================================
// CONFIGURATION & LOGGING
// ============================================================================

const { CHAT_HISTORY_FETCH_LIMIT } = SYNC_CONSTANTS;
const { log, logTime, flushLogs } = createLogger('MessageSync');

// ============================================================================
// INTERNAL HELPERS
// ============================================================================

const updateLastSyncMessagesTime = (chatId, time) => storage.setItem(`lastSyncMessagesTime_${chatId}`, time);

const consolidateMessages = (messages, chatJid) => messages.map(msg => ({ ...msg, chatJid }));

const reportProgress = (current, total, enabled) => {
   if (!enabled) return;
   const progress = Math.min(100, Math.round((current / total) * 100));
   store.dispatch(setSyncProgress(progress));
};

// ============================================================================
// MEDIA BOUNDARY HELPERS
// ============================================================================

/** Collect parent messages for the replied-messages store. */
const collectReplied = msgs => msgs.filter(m => m?.msgBody?.parentMessage).map(m => m.msgBody.parentMessage);

// ============================================================================
// STRATEGY
// ============================================================================

const getStrategy = ({ unreadCount, unreadMessageId } = {}) => {
   if (unreadCount === -1) return { down: false, up: true, anchorId: '' };
   if (unreadMessageId && unreadCount > 0) return { down: true, up: true, anchorId: unreadMessageId };
   if (unreadMessageId && unreadCount === 0) return { down: false, up: true, anchorId: unreadMessageId };
   if (!unreadMessageId && unreadCount > 0) return { down: true, up: false, anchorId: '' };
   return { down: false, up: true, anchorId: '' };
};

// ============================================================================
// CORE PRIMITIVES
// ============================================================================

export const fetchFromAPI = async ({ chatJid, direction, anchorId, limit = CHAT_HISTORY_FETCH_LIMIT }) => {
   try {
      const response = await getChatMessages({
         toJid: chatJid,
         position: direction,
         lastMessageId: anchorId,
         limit,
         includeMsgObj: true,
         sendSeen: false,
      });

      if (response?.statusCode === 200) {
         response.inviteStatus = Number(response.inviteStatus ?? 0);
         response.remainingMessages = response.remainingMessages ?? response.data?.remainingMessages;
         response.isSender = response.isSender ?? response.data?.isSender;
      }
      return response;
   } catch (err) {
      log('fetchFromAPI', `Error (${direction}): ${err.message}`);
      return { statusCode: 500, data: [] };
   }
};

/**
 * syncToRedux — group a new page of messages and dispatch to the Redux slice.
 * Cross-boundary grouping is handled inside addMessages / prependMessages reducers.
 */
const syncToRedux = async ({ chatId, messages, hasMoreOlder, hasMoreNewer, direction = 'up' }) => {
   if (!chatId) return;

   if (!messages?.length) {
      store.dispatch(setConversationHasMore({ chatId, hasMoreOlder: false, hasMoreNewer: false }));
      return;
   }

   const grouped = groupMediaMessages(deduplicateMessages(messages));
   const processed = insertDateLabels(grouped);

   const replied = collectReplied(processed);
   if (replied.length > 0) store.dispatch(setRepliedMessages(replied));

   const payload = { chatId, messages: processed };
   if (direction === 'down') {
      if (hasMoreNewer !== undefined) payload.hasMoreNewer = hasMoreNewer;
      store.dispatch(prependMessages(payload));
   } else {
      if (hasMoreOlder !== undefined) payload.hasMoreOlder = hasMoreOlder;
      store.dispatch(addMessages(payload));
   }
};

const bulkSyncToRedux = async ({ chatId, messages, hasMoreOlder, hasMoreNewer }) => {
   if (!chatId) return;

   if (!messages?.length) {
      store.dispatch(setConversationHasMore({ chatId, hasMoreOlder: false, hasMoreNewer: false }));
      return;
   }

   const deduped = deduplicateMessages(messages);
   const grouped = groupMediaMessages(deduped);
   const processed = insertDateLabels(grouped);

   const repliedMessagesToStore = deduped
      .filter(msg => msg?.msgBody?.parentMessage)
      .map(msg => msg.msgBody.parentMessage);
   if (repliedMessagesToStore.length > 0) {
      store.dispatch(setRepliedMessages(repliedMessagesToStore));
   }

   store.dispatch(
      setConversationMessages({
         chatId,
         messages: processed,
         hasMoreOlder,
         hasMoreNewer,
      }),
   );
};

// ============================================================================
// PUBLIC API
// ============================================================================

export const syncMessages = async (chats, config = {}) => {
   if (!chats?.length) return;

   const { preFetch } = config;

   const BATCH_SIZE = 10;
   const results = [];

   if (preFetch) store.dispatch(setSyncProgress(0));

   for (let i = 0; i < chats.length; i += BATCH_SIZE) {
      const batch = chats.slice(i, i + BATCH_SIZE);
      const batchResults = await Promise.allSettled(batch.map(chat => processChat(chat)));
      results.push(...batchResults);
      reportProgress(i + batch.length, chats.length, preFetch);
   }

   if (preFetch) store.dispatch(setSyncProgress(100));

   return {
      total: chats.length,
      successful: results.filter(r => r.status === 'fulfilled').length,
      failed: results.filter(r => r.status === 'rejected').length,
      results,
   };
};

export const processChat = async (chat = {}) => {
   const { fromUserId, chatType } = chat;
   const chatJid = formatUserIdToJid(fromUserId, chatType);
   const chatId = getUserIdFromJid(chatJid);
   const localUserId = getLocalUserId();

   log('processChat', `🚀 ${fromUserId}`, chat);
   const t0 = performance.now();

   try {
      const strategy = getStrategy(chat);
      log('processChat', 'Strategy', strategy);
      log('processChat', `chat.unreadMessageId: ${chat.unreadMessageId}`);
      log('processChat', `strategy.anchorId: ${strategy.anchorId}`);
      log('processChat', `chat.unreadCount: ${chat.unreadCount}`);

      log('Step 1', 'API Fetch (Initializing)');

      let downApiResults = [];
      let upApiResults = [];
      let finalHasMoreOlder = true;
      let finalHasMoreNewer = false;

      if (strategy.down) {
         log('Step 1', 'API Fetch (Down)');
         const res = await fetchFromAPI({ chatJid, direction: 'down', anchorId: strategy.anchorId });
         if (res.statusCode === 200) {
            downApiResults = res?.data || [];
            log('processChat', `downApiResults count: ${downApiResults.length}`);

            // Dispatch invite status and message count
            if (res.inviteStatus !== undefined && res.inviteStatus !== null) {
               store.dispatch(
                  updateInviteStatus({
                     userId: chatId,
                     inviteStatus: res.inviteStatus,
                     remainingMessages: res.remainingMessages,
                     isSender: res.isSender,
                  }),
               );
            }
         }
         if (downApiResults.length > 0 && chat.unreadCount > 0) {
            const position = downApiResults.findIndex(message => {
               const senderId = getMessageSenderId({ type: 'chats', message });
               const isLocalUser = senderId === localUserId;

               if (isLocalUser) return true;

               return message.msgStatus === 2;
            });
            let separator = {
               type: 'unread-separator',
               label: i18n.t('message.UNREAD_MESSAGE_SINGULAR'),
               msgId: chat.unreadMessageId + '-unread-separator',
               timestamp: downApiResults[downApiResults.length - 1].timestamp + 1,
            };
            if (position !== -1) {
               separator.timestamp = downApiResults[position].timestamp + 1;
               downApiResults.splice(position, 0, separator);
               log('processChat', `✅ unread separator inserted at position: ${position}`);
            } else {
               downApiResults.push(separator);
               log('processChat', `✅ unread separator pushed at end, timestamp: ${separator.timestamp}`);
            }
         }
         finalHasMoreNewer = downApiResults.length >= CHAT_HISTORY_FETCH_LIMIT;
      }

      if (strategy.up) {
         log('Step 1', 'API Fetch (Up)');
         const res = await fetchFromAPI({ chatJid, direction: 'up', anchorId: strategy.anchorId });
         if (res.statusCode === 200) {
            upApiResults = res?.data || [];
            finalHasMoreOlder = upApiResults.length >= CHAT_HISTORY_FETCH_LIMIT;

            // Dispatch invite status and message count
            if (res.inviteStatus !== undefined && res.inviteStatus !== null) {
               store.dispatch(
                  updateInviteStatus({
                     userId: chatId,
                     inviteStatus: res.inviteStatus,
                     remainingMessages: res.remainingMessages,
                     isSender: res.isSender,
                  }),
               );
            }
            /**
            // if (storeInDB && !finalHasMoreOlder) {
            //    await markAllMessagesFetched(chatId, 'up')
            // }
             */
         }
      }

      const allApiResults = [...downApiResults, ...upApiResults];

      log('Step 2', 'Consolidated Redux Update');
      const consolidatedApi = consolidateMessages(allApiResults, chatJid);
      const finalMsgs = [...consolidatedApi];

      await bulkSyncToRedux({
         chatId,
         messages: finalMsgs,
         hasMoreOlder: finalHasMoreOlder,
         hasMoreNewer: finalHasMoreNewer,
      });

      updateLastSyncMessagesTime(chatId, Date.now());
      logTime('Summary', '✅ DONE', t0);
      return { fetched: allApiResults.length, total: consolidatedApi.length };
   } catch (err) {
      log('Error', `processChat failed: ${err.message}`);
      return { error: true };
   } finally {
      flushLogs(`[MessageSync] PROCESS_CHAT DONE: ${chatId} ${((performance.now() - t0) / 1000).toFixed(2)}s`);
   }
};

export const fetchOlderMessages = async (chatJid, anchorId) => {
   const chatId = getUserIdFromJid(chatJid);

   log('fetchOlderMessages', `🚀 ${chatJid}`);
   const t0 = performance.now();

   try {
      log('Step 1', 'API Query (Up)');
      const res = await fetchFromAPI({ chatJid, direction: 'up', anchorId });
      const apiMessages = res.statusCode === 200 ? res.data || [] : [];
      const count = apiMessages.length;
      const hasMoreOlder = count >= CHAT_HISTORY_FETCH_LIMIT;

      const consolidated = consolidateMessages(apiMessages, chatJid);
      await syncToRedux({ chatId, messages: consolidated, anchorId, direction: 'up', hasMoreOlder });

      logTime('Summary', `✅ ${count} fetched`, t0);
      return count;
   } catch (err) {
      log('Error', `fetchOlderMessages failed: ${err.message}`);
      return 0;
   } finally {
      flushLogs(`[MessageSync] FETCH_OLDER DONE: ${((performance.now() - t0) / 1000).toFixed(2)}s`);
   }
};

export const fetchNewerMessages = async (chatJid, anchorId) => {
   const chatId = getUserIdFromJid(chatJid);

   log('fetchNewerMessages', `🚀 ${chatJid}`);
   const t0 = performance.now();

   try {
      log('Step 1', 'API Query (Down)');
      const res = await fetchFromAPI({ chatJid, direction: 'down', anchorId });
      const apiMessages = res.statusCode === 200 ? res.data || [] : [];
      const count = apiMessages.length;
      const hasMoreNewer = count >= CHAT_HISTORY_FETCH_LIMIT;

      const consolidated = consolidateMessages(apiMessages, chatJid);
      await syncToRedux({
         chatId,
         messages: [...consolidated],
         anchorId,
         direction: 'down',
         hasMoreNewer,
      });

      logTime('Summary', `✅ ${count} fetched`, t0);
      return count;
   } catch (err) {
      log('Error', `fetchNewerMessages failed: ${err.message}`);
      return 0;
   } finally {
      flushLogs(`[MessageSync] FETCH_NEWER DONE: ${((performance.now() - t0) / 1000).toFixed(2)}s`);
   }
};
